import React, { useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import useModalEscape from '../../hooks/useModalEscape';
import useModalTransition from '../../hooks/useModalTransition';
import { notifyError, notifySuccess } from '../../notify';
import { readControlCenterResponse } from './controlCenterApi';

const VARIABLES_PER_PAGE = 7;

export default function RenderEnvironment({ target, configured }) {
  const { getIdToken } = useAuth();
  const tokenRef = useRef(getIdToken);
  tokenRef.current = getIdToken;
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [editor, setEditor] = useState(null);
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [deploy, setDeploy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [revealed, setRevealed] = useState(null);
  const [revealingKey, setRevealingKey] = useState(null);
  const revealRequestRef = useRef(null);
  const revealTimerRef = useRef(null);
  const keyRef = useRef(null);
  const valueRef = useRef(null);
  const shouldRenderEditor = useModalTransition(Boolean(editor));
  const serviceName = target === 'mobile' ? 'Mobile App Backend' : 'Admin Dashboard Backend';
  const totalPages = Math.max(1, Math.ceil(variables.length / VARIABLES_PER_PAGE));
  const page = Math.min(currentPage, totalPages);
  const visibleVariables = variables.slice((page - 1) * VARIABLES_PER_PAGE, page * VARIABLES_PER_PAGE);
  const firstPageButton = Math.max(1, Math.min(page - 2, totalPages - 4));
  const pageButtons = Array.from({ length: Math.min(totalPages, 5) }, (_, index) => firstPageButton + index);

  useEffect(() => { setCurrentPage(1); }, [target]);

  useEffect(() => {
    revealRequestRef.current?.abort();
    clearTimeout(revealTimerRef.current);
    setRevealed(null);
    setRevealingKey(null);
    return () => {
      revealRequestRef.current?.abort();
      clearTimeout(revealTimerRef.current);
    };
  }, [target, page, refresh]);

  function hideValue() {
    revealRequestRef.current?.abort();
    clearTimeout(revealTimerRef.current);
    setRevealingKey(null);
    setRevealed(null);
  }

  async function showValue(name) {
    hideValue();
    const controller = new AbortController();
    revealRequestRef.current = controller;
    const timeout = setTimeout(() => controller.abort(), 20000);
    setRevealingKey(name);
    try {
      const token = await tokenRef.current();
      if (controller.signal.aborted) return;
      const params = new URLSearchParams({ target, key: name });
      const response = await fetch(`${API_BASE_URL}/api/control-center/env-var-value?${params}`, {
        headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
      });
      const data = await readControlCenterResponse(response);
      if (!response.ok || !data.ok || typeof data.value !== 'string') throw new Error(data.error || 'Could not view this variable.');
      if (revealRequestRef.current !== controller) return;
      setRevealed({ key: name, value: data.value });
      revealTimerRef.current = setTimeout(() => setRevealed(null), 30000);
    } catch (err) {
      if (err.name !== 'AbortError' && revealRequestRef.current === controller) notifyError(err.message || 'Could not view this variable.');
    } finally {
      clearTimeout(timeout);
      if (revealRequestRef.current === controller) { revealRequestRef.current = null; setRevealingKey(null); }
    }
  }

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    setVariables([]);
    setError(false);
    setLoading(true);
    if (!configured) { clearTimeout(timeout); setLoading(false); return () => { active = false; controller.abort(); }; }
    async function load() {
      try {
        const token = await tokenRef.current();
        const response = await fetch(`${API_BASE_URL}/api/control-center/env-vars?target=${target}`, {
          headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
        });
        const data = await readControlCenterResponse(response);
        if (!response.ok || !data.ok) throw new Error(data.error || 'Could not load environment variables.');
        if (active) setVariables(data.variables || []);
      } catch (err) {
        if (active) { setError(true); notifyError(err.message || 'Could not load environment variables.'); }
      } finally { clearTimeout(timeout); if (active) setLoading(false); }
    }
    load();
    return () => { active = false; controller.abort(); clearTimeout(timeout); };
  }, [target, configured, refresh]);

  useEffect(() => {
    if (editor && shouldRenderEditor) (editor === 'add' ? keyRef : valueRef).current?.focus();
  }, [editor, shouldRenderEditor]);

  function closeEditor() {
    if (saving) return;
    setEditor(null);
    setValue('');
    setDeploy(false);
  }
  useModalEscape(closeEditor, Boolean(editor));

  function openEditor(variable) {
    setKey(variable?.key || '');
    setValue('');
    setDeploy(false);
    setEditor(variable ? 'edit' : 'add');
  }

  async function save(event) {
    event.preventDefault();
    const name = key.trim();
    if (!/^[A-Za-z_][A-Za-z0-9_]{0,127}$/.test(name) || !value.length || value.length > 8192) {
      notifyError('Enter a valid variable name and a value of up to 8,192 characters.');
      return;
    }
    setSaving(true);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 45000);
    try {
      const token = await tokenRef.current();
      const response = await fetch(`${API_BASE_URL}/api/control-center/env-vars`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ target, key: name, value, deploy, confirmation: `save-env-${target}` }),
        signal: controller.signal,
      });
      const data = await readControlCenterResponse(response);
      if (data.saved) {
        if (response.ok && data.deployRequested) notifySuccess(`${name} saved. Render accepted the deploy request for ${serviceName}.`);
        else if (response.ok) notifySuccess(`${name} saved on Render. Deploy ${serviceName} to apply it.`);
        else notifyError(data.error || `${name} was saved, but the deploy could not be confirmed.`);
        setEditor(null);
        setValue('');
        setRefresh(count => count + 1);
      } else if (!response.ok || !data.ok) {
        throw new Error(data.error || 'Render did not save the variable.');
      }
    } catch (err) {
      notifyError(err.name === 'AbortError' ? 'Save status is unconfirmed. Check Render before retrying.' : err.message || 'Could not save the variable.');
    } finally { clearTimeout(timeout); setSaving(false); }
  }

  return <>
    <div className="cc-environment">
      <div className="cc-environment-heading"><div><h2>Environment variables</h2><p>{serviceName} · values hidden until viewed</p></div>
        <button className="cc-button" disabled={!configured || loading || error} onClick={() => { hideValue(); openEditor(null); }}>Add variable</button>
      </div>
      <div className="cc-environment-content">
      {!configured ? <p className="cc-environment-empty">Configure this service in Render to manage its variables.</p>
        : loading ? <p className="cc-environment-empty">Loading variable names…</p>
          : error ? <div className="cc-environment-empty">Could not load variable names. <button className="cc-inline-button" onClick={() => setRefresh(count => count + 1)}>Try again</button></div>
            : variables.length ? <><div className="cc-variable-list">{visibleVariables.map(variable => <div className="cc-variable-row" key={variable.key}>
              <span className="cc-variable-name">{variable.key}</span><span className={revealed?.key === variable.key ? 'cc-variable-value' : 'cc-variable-hidden'}>{revealed?.key === variable.key ? revealed.value : 'Value hidden'}</span>
              {variable.editable ? <span className="cc-variable-actions"><button className="cc-inline-button" disabled={revealingKey === variable.key} onClick={() => revealed?.key === variable.key ? hideValue() : showValue(variable.key)}>{revealed?.key === variable.key ? 'Hide' : 'View'}</button><button className="cc-inline-button" onClick={() => { hideValue(); openEditor(variable); }}>Edit</button></span>
                : <span className="cc-variable-managed" title="Service-control credentials and service IDs must be changed in Render.">Protected setting</span>}
            </div>)}</div><div className="cc-env-pagination">
              <span className="cc-env-pagination-info">Page {page} of {totalPages}</span>
              <div className="cc-env-pagination-actions">
                <button type="button" className="cc-env-pagination-btn" disabled={page === 1} onClick={() => setCurrentPage(page - 1)}>Previous</button>
                {pageButtons.map(number => <button type="button" key={number} className={`cc-env-pagination-btn ${page === number ? 'active' : ''}`} aria-label={`Page ${number}`} aria-current={page === number ? 'page' : undefined} onClick={() => setCurrentPage(number)}>{number}</button>)}
                <button type="button" className="cc-env-pagination-btn" disabled={page === totalPages} onClick={() => setCurrentPage(page + 1)}>Next</button>
              </div>
            </div></> : <p className="cc-environment-empty">No service-level variables found. Add one to get started.</p>}
      </div>
      <p className="cc-environment-note">Viewed values hide after 30 seconds. Only service-level variables appear here. Service-control credentials, service IDs, and environment groups stay in Render.</p>
    </div>
    {shouldRenderEditor && <div className={`cc-overlay modal-transition-${editor ? 'enter' : 'exit'}`} aria-hidden={!editor} onClick={closeEditor}>
      <form className="cc-dialog cc-env-dialog" role="dialog" aria-modal="true" aria-labelledby="cc-env-title" onClick={event => event.stopPropagation()} onSubmit={save}>
        <div className="cc-dialog-header"><h2 id="cc-env-title">{editor === 'add' ? 'Add variable' : 'Update variable'}</h2><p>{serviceName}</p></div>
        <label className="cc-env-field">Variable name<input ref={keyRef} value={key} disabled={editor !== 'add' || saving} maxLength={128} autoComplete="off" onChange={event => setKey(event.target.value)} placeholder="VARIABLE_NAME" required /></label>
        <label className="cc-env-field">New value<input ref={valueRef} type="password" value={value} disabled={saving} maxLength={8192} autoComplete="new-password" onChange={event => setValue(event.target.value)} placeholder="Enter a new value" required /></label>
        <p className="cc-env-help">Existing values are never shown. Saving replaces this variable on Render.</p>
        <label className="cc-env-deploy"><input type="checkbox" checked={deploy} disabled={saving} onChange={event => setDeploy(event.target.checked)} />Build and deploy {serviceName} after saving</label>
        <p className="cc-env-help">This builds the latest commit on Render. Without a deploy, the running service keeps its current value. Deploying the admin backend may briefly disconnect this page.</p>
        <div className="cc-dialog-actions"><button type="button" className="cc-button" disabled={saving} onClick={closeEditor}>Cancel</button><button type="submit" className="cc-button cc-primary" disabled={saving}>{saving ? 'Saving…' : deploy ? 'Save and deploy' : 'Save variable'}</button></div>
      </form>
    </div>}
  </>;
}
